import Icon from '@/components/icons/Icon';
import { SCREEN_NAME } from '@/constants/analytics';
import { APP_RATING } from '@/constants/downloadApp';
import { APP_ROUTES } from '@/constants/pages';
import { SMART_LINKS } from '@/constants/smartLinkConstants';
import { isAndroidPhone, isIPhone, isServer, isSistaminuten } from '@/helpers';
import { openAppOrStore } from '@/helpers/smartLinks';
import { useAppSelector } from '@/hooks';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s } from '@/locale';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Button } from '../forms/buttons';
import { ListItem, ListItemContent } from '../lists';
import Alert from '../notifications/Alert/Alert';
import Rating from '@/components/elements/redesign/Rating/Rating';
import styles from './AppPromotionCard.module.scss';
import Card from './Card';

const baseTranslationKey = 'components.elements.Card.AppPromotionCard';
const LOCAL_STORAGE_BASE_KEY = 'appPromoDismissed';

const getLocalStorageKey = (triggerSource: string) => {
  return `${LOCAL_STORAGE_BASE_KEY}.${triggerSource}`;
};

const getTrackingTriggerSource = (pathname: string): (typeof SCREEN_NAME)[keyof typeof SCREEN_NAME] | undefined => {
  switch (pathname) {
    case '/bokningar/tidigare':
      return 'my_bookings_finished';
    case '/bokningar':
      return 'my_bookings_upcoming';
    case '/favoriter':
      return 'favorites';
    case '/places':
      return 'place_landing';
    case '/':
      return 'home';
    default:
      Sentry.captureMessage(`Unknown trigger source (${pathname}) for general app promotion banner`);
      return undefined;
  }
};

const pathToStringMapping = {
  '/bokningar/tidigare': SMART_LINKS.BOOKINGS_FINISHED,
  '/bokningar': SMART_LINKS.BOOKINGS,
  '/favoriter': SMART_LINKS.FAVORITES,
  '/places': SMART_LINKS.BASE_URL,
  '/': SMART_LINKS.HOME,
} as const;

// Logged out, these pages render their own app download prompt in place of the content.
const pagesWithOwnLoggedOutPrompt = [APP_ROUTES.MY_BOOKINGS, APP_ROUTES.MY_BOOKINGS_FINISHED, APP_ROUTES.MY_FAVORITES];

// Promo bottom sheets would stack on top of that prompt, so every promo card checks this.
export const pageHasOwnAppPrompt = (pathname: string, isLoggedIn: boolean) =>
  !isLoggedIn && pagesWithOwnLoggedOutPrompt.some((path) => matchPath(pathname, { path, exact: true }));

const getPageSmartLink = (pathname: string) => {
  const matchedPath = Object.keys(pathToStringMapping).find((path) => pathname === path);
  return matchedPath ? pathToStringMapping[matchedPath] : SMART_LINKS.HOME;
};

export const checkIfDismissedInSession = () => {
  if (isServer) return true;
  if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
    return true;
  }

  return false;
};

const checkIfDismissedWithinTimeperiod = (days: number, triggerSource: string) => {
  const dismissedTimestampString = localStorage.getItem(getLocalStorageKey(triggerSource));
  const dismissedTimestamp = dismissedTimestampString ? Number(dismissedTimestampString) : null;
  const oneDayInMs = days * 24 * 60 * 60 * 1000;
  const currentTime = new Date().getTime();
  if (!dismissedTimestamp) {
    return false;
  }

  // Dismissed more than 1 day ago
  if (currentTime - dismissedTimestamp >= oneDayInMs) {
    localStorage.setItem(getLocalStorageKey(triggerSource), Date.now().toString());
    return false;
  }

  return true;
};

const isGCPage = (pathname: string) => {
  return /^\/places\/[^/]+\/(valuecards|giftcards)\/(buy|confirmation)$/.test(pathname);
};

const AppPromotionCard = () => {
  const location = useLocation();
  const { user } = useAppSelector((state) => state.users);
  const hasSavedGiftcard = sessionStorage.getItem('savedGiftcard') === 'true';

  const pathname = (() => {
    if (location.pathname.startsWith('/places/') && !isGCPage(location.pathname)) {
      return '/places';
    }
    return location.pathname;
  })();

  const [hasDismissed, setHasDismissed] = useState(false);
  const isLoggedIn = Boolean(user?.token);
  const isWhiteListedPage =
    Object.keys(pathToStringMapping).some((page) => page === pathname) && !pageHasOwnAppPrompt(pathname, isLoggedIn);
  const triggerSource = isWhiteListedPage ? getTrackingTriggerSource(pathname) : undefined;

  const hasDismissedEarlier = (() => {
    if (isServer) return true;
    /**
     * Check if user has already dismissed the AppPromotion during the session
     * - if dismissed during session, dont show any banner on any of the pages for the remaining session
     */
    if (Boolean(sessionStorage.getItem(LOCAL_STORAGE_BASE_KEY))) {
      return true;
    }

    /**
     * - if not dismissed during session, check if the page specific banner has been dismissed
     */
    return checkIfDismissedWithinTimeperiod(1, triggerSource);
  })();

  const [timerTriggered, setTimerTriggered] = useState(false);
  const showCard =
    !isServer &&
    isWhiteListedPage &&
    !isSistaminuten() &&
    (isIPhone() || isAndroidPhone()) &&
    (!hasDismissedEarlier || hasSavedGiftcard) &&
    !hasDismissed &&
    timerTriggered;

  useTrackScreenShown(
    {
      screen_name: SCREEN_NAME.NEW_DOWNLOAD_APP_BANNER,
      initiated_from: showCard ? triggerSource : undefined,
    },
    [showCard],
    [showCard],
  );

  useEffect(() => {
    const setNewTimeout = () => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        setTimerTriggered(true);
      }, 2000);
    };

    let timer = setTimeout(() => {
      setTimerTriggered(true);
    }, 2000);

    setNewTimeout();

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleDismiss = () => {
    setHasDismissed(true);
    localStorage.setItem(getLocalStorageKey(triggerSource), Date.now().toString());
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    sessionStorage.removeItem('savedGiftcard');
  };

  const handleDownloadAppClick = () => {
    localStorage.setItem(getLocalStorageKey(triggerSource), Date.now().toString());
    sessionStorage.setItem(LOCAL_STORAGE_BASE_KEY, 'dismissed');
    sessionStorage.removeItem('savedGiftcard');
    let url = getPageSmartLink(pathname);

    if (pathname === '/places') {
      url = SMART_LINKS.getPlaceSmartLink(window.location.href); // url += `?deeplink=${window.location.href}`;
    }

    openAppOrStore(url);
  };

  return showCard ? (
    <>
      <div className={`z-10003 fixed h-screen w-screen bg-black opacity-50`}></div>
      <Card bottom closable={false} className={`z-10004 fixed bottom-0 ${styles.slideInBottom}`}>
        <div className="gap-lg flex flex-col">
          <div className="gap-xl flex min-w-full flex-col">
            <div className="gap-lg flex">
              <div>
                <div className="p-md rounded-md bg-[#23A994]">
                  <Icon variant="bokadirekt" size="md" color="white" />
                </div>
              </div>
              <div className="gap-sm flex flex-col">
                <h2 className="text-md font-bold">{_s(`${baseTranslationKey}.title`)}</h2>
                <Rating avgRating={APP_RATING.score} size="lg" ratingCount={APP_RATING.count} />
              </div>
            </div>
            {hasSavedGiftcard && (
              <Alert
                leftSlot={<Icon size="sm" variant="info-circle" />}
                variant="information"
                verticalAlign="top"
                rightSlot={<p>{_s(`${baseTranslationKey}.contentList.savedGiftcard`)}</p>}
              />
            )}
            <ul className="gap-sm flex flex-col">
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent subTitle={_s(`${baseTranslationKey}.contentList.item1`)} />
              </ListItem>
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent subTitle={_s(`${baseTranslationKey}.contentList.item2`)} />
              </ListItem>
              <ListItem
                className="!py-[0]"
                leftPadding={false}
                leftSlot={<Icon color="brand-500" variant="checkbox-checked-round" size="sm" />}>
                <ListItemContent subTitle={_s(`${baseTranslationKey}.contentList.item3`)} />
              </ListItem>
            </ul>
          </div>
          <div className="gap-sm flex flex-col">
            <Button
              variant="primary"
              onClick={handleDownloadAppClick}
              block
              label={_s(`${baseTranslationKey}.cta`)}
              size="lg"
            />
            <Button variant="link" onClick={handleDismiss} block label={_s(`${baseTranslationKey}.link`)} size="sm" />
          </div>
        </div>
      </Card>
    </>
  ) : null;
};

export default AppPromotionCard;
